import env from "../../services/AppEnv";
/**
 * Helpers for the localized field controls. Localized values are transferred as
 * `ch.astina.paloma.common.localization.Localized`, i.e.
 * `{values: [{locale: 'de-CH', value: 'Schuh'}, ...]}`, and the locales to offer
 * an input for come from `options.locales` (see `LocalizedOptions` on the server).
 */
/**
 * Locale strings are compared leniently, because `options.locales` and the
 * locales inside the value are not guaranteed to use the same separator
 * (`de_CH` vs `de-CH`) or the same casing.
 */
export function normalizeLocale(locale) {
    return (locale || '').replace(/_/g, '-').toLowerCase();
}
export function sameLocale(a, b) {
    return normalizeLocale(a) === normalizeLocale(b);
}
/**
 * The locales to render an input for: those declared by the field, falling back
 * to the locales already present in the value and finally to the UI locale, so
 * the control never renders without a single input.
 */
export function resolveLocales(options, data) {
    const declared = options.locales;
    if (declared && declared.length > 0) {
        return [...declared];
    }
    const fromValue = entriesOf(data)
        .map(entry => entry.locale)
        .filter(locale => !!locale);
    if (fromValue.length > 0) {
        return fromValue;
    }
    return [env.locale];
}
/**
 * A `locale -> value` map to bind the inputs against.
 */
export function toValueMap(data, locales) {
    const entries = entriesOf(data);
    const map = {};
    locales.forEach(locale => {
        const entry = entries.find(e => sameLocale(e.locale, locale));
        map[locale] = entry ? entry.value : null;
    });
    return map;
}
/**
 * Rebuilds the `Localized` value from the bound map. Locales that are present in
 * the original value but not rendered (because the field does not declare them)
 * are kept, so editing a form never drops translations for a locale the current
 * channel configuration no longer lists.
 */
export function toLocalizedValues(data, locales, values) {
    const kept = entriesOf(data)
        .filter(entry => !locales.some(locale => sameLocale(entry.locale, locale)));
    const edited = locales.map(locale => ({
        locale: locale,
        value: emptyToNull(values[locale]),
    }));
    return { values: [...kept, ...edited] };
}
/**
 * Structural check, unlike `LocalizedUtils.isLocalizedValue` it also recognizes a
 * value that has no entries yet.
 */
export function isLocalizedValue(data) {
    return !!data
        && typeof data === 'object'
        && !Array.isArray(data)
        && Array.isArray(data.values);
}
export function isEmptyLocalizedValue(data) {
    return entriesOf(data).every(entry => emptyToNull(entry.value) === null);
}
function entriesOf(data) {
    const values = data && data.values;
    return Array.isArray(values) ? values : [];
}
function emptyToNull(value) {
    return value === undefined || value === null || value === '' ? null : value;
}
